import React from "react";
import { GetStaticProps } from "next";
import PageLayout from "lib/components/PageLayout";
import Wrapper from "lib/components/Wrapper";
import Box from "@material-ui/core/Box";
import useIsResMobile from "lib/hooks/useIsResMobile";
import Grid from "@material-ui/core/Grid";
import CardResource from "domain/resources/CardResource";
import ResourcesService from "domain/resources/resourcesService";
import Head from "lib/components/Head";
import SearchForm from "domain/resources/SearchForm";

interface ResourcesPageProps {
  data: {
    categoryName: string;
    categoryTitle: string;
    resources: ResourceMeta[];
  }[];
}

const ResourcesPage: React.FC<ResourcesPageProps> = ({ data }) => {
  const isResMobile = useIsResMobile();

  return (
    <PageLayout>
      <Head
        title="Immigration Resources | Lawfully"
        desc="Find all your U.S immigration related resources here at Lawfully"
        imageUrl="/how-to-become-a-us-citizen-through-naturalization.png"
        url="/resources"
      />

      <Wrapper
        css={`
          max-width: 1040px;
        `}
      >
        {/* Title */}
        <Box
          mt={isResMobile ? 2 : 8}
          mb={isResMobile ? 16 : 32}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent={isResMobile ? "flex-start" : "center"}
        >
          <h1
            css={`
              margin: 0;
              font-weight: bold;
              font-size: ${isResMobile ? 30 : 50}px;
              line-height: ${isResMobile ? 42 : 70}px;
            `}
          >
            Immigration Resources
          </h1>
          <h2
            css={`
              margin-bottom: 60px;
              font-weight: 500;
              font-size: ${isResMobile ? 16 : 20}px;
              line-height: ${isResMobile ? 24 : 28}px;
              text-align: center;
            `}
          >
            Find all your U.S immigration related resources here at Lawfully
          </h2>

          <SearchForm />

          {data.map((categoryGroup) => {
            if (categoryGroup.resources.length === 0) return null;
            return (
              <div
                key={categoryGroup.categoryTitle}
                css={`
                  margin-top: ${isResMobile ? "80px" : "100px"};
                  width: 100%;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                `}
              >
                <h3
                  css={`
                    text-transform: capitalize;
                    font-weight: bold;
                    font-size: ${isResMobile ? "20px" : "24px"};
                    line-height: ${isResMobile ? "28px" : "34px"};
                    margin-bottom: 40px;
                  `}
                >
                  {categoryGroup.categoryTitle}
                </h3>
                <Box
                  width="100%"
                  display="grid"
                  gridTemplateColumns={isResMobile ? "1fr" : "repeat(3, 1fr)"}
                  gridRowGap="40px"
                  gridColumnGap="20px"
                >
                  {categoryGroup.resources.map((resource) => (
                    <CardResource
                      key={resource.slug}
                      slug={resource.slug}
                      title={resource.title}
                      imageUrl={resource.mainImageUrl}
                      imageAlt={resource.mainImageAlt}
                    />
                  ))}
                </Box>
              </div>
            );
          })}
        </Box>
      </Wrapper>
    </PageLayout>
  );
};

export default ResourcesPage;

export const getStaticProps: GetStaticProps = async ({ preview }) => {
  const resourcesService = new ResourcesService();
  const data = await resourcesService.getListResourceGrouped({ preview });

  return {
    props: {
      data,
    },
    revalidate: 10,
  };
};
